import type { Subject } from 'rxjs';
import { ReplaySubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Logger } from '@bayer-int/portletce-shared';
import { noop, isString, invert } from 'lodash-es';
import { nanoid } from 'nanoid';
import * as t from 'io-ts';

import type { ExternalAppAction } from './store';
import { AppAction } from './store';
import type PortletCE from './PortletCE.wc.svelte';
import type { VoidCallback } from './common/components/portletMenu/types';
import { FilterValue } from './models';
import { applyFilterMapping, forwardFilter } from './instanceApi';

const logger = Logger.default();

export function createSubject(name = nanoid()) {
    window.portletce = window.portletce || {};
    window.portletce[name] = window.portletce[name] || new ReplaySubject<ExternalAppAction>();

    const subject = window.portletce[name];

    return {
        events$: subject,
        receiveFiltersFrom(portlet: unknown, mapping?: unknown): VoidCallback {
            if (!isPortletCE(portlet)) {
                logger.error('Expected instance of `PortletCE`, got: ', portlet);
                return noop;
            }
            if (mapping !== undefined && !t.record(t.string, t.string).is(mapping)) {
                logger.error(
                    'Invalid mapping argument, expected Record<string, string>, got: ',
                    mapping,
                );
                return noop;
            }

            return portlet.emitEventsTo(subject, undefined, mapping);
        },

        forwardFiltersTo(portlet: unknown, mapping?: unknown): VoidCallback {
            if (!isPortletCE(portlet)) {
                logger.error('Expected instance of `PortletCE`, got: ', portlet);
                return noop;
            }
            if (mapping !== undefined && !t.record(t.string, t.string).is(mapping)) {
                logger.error(
                    'Invalid mapping argument, expected Record<string, string>, got: ',
                    mapping,
                );
                return noop;
            }

            return portlet.subscribeEventsFrom(
                subject.pipe(
                    filter(({ sender }) => sender !== undefined && sender !== portlet.uniqueId()),
                    forwardFilter,
                    applyFilterMapping(invert(mapping || {})),
                ),
            );
        },

        attach(portlet: unknown, mapping?: unknown): VoidCallback {
            if (!isPortletCE(portlet)) {
                logger.error('Expected instance of `PortletCE`, got: ', portlet);
                return noop;
            }

            const subscriptionToExternalCleanup = this.forwardFiltersTo(portlet, mapping);
            const subscriptionOfExternalCleanup = this.receiveFiltersFrom(portlet, mapping);

            return () => {
                subscriptionToExternalCleanup();
                subscriptionOfExternalCleanup();
            };
        },

        dispatchFilter: createDispatchFilterWithParser(subject, FilterValue.tryParse, name),
        dispatchFilterOfType: {
            dimension: createDispatchFilterWithParser(subject, FilterValue.tryParseDimension, name),
            date: createDispatchFilterWithParser(subject, FilterValue.tryParseDate, name),
            dateRange: createDispatchFilterWithParser(subject, FilterValue.tryParseDateRange, name),
            hierarchy: createDispatchFilterWithParser(subject, FilterValue.tryParseHierarchy, name),
            scale: createDispatchFilterWithParser(subject, FilterValue.tryParseScale, name),
            smartViewTag: createDispatchFilterWithParser(
                subject,
                FilterValue.tryParseSmartViewTag,
                name,
            ),
            role: createDispatchFilterWithParser(subject, FilterValue.tryParseRole, name),
            number: createDispatchFilterWithParser(subject, FilterValue.tryParseNumber, name),
        },
    };
}

/**
 * Since svelte4, custom properties and methods on HTMLCustomElement are not yet available
 * until the component mounts. We add a `tick` function to package public api class in order to
 * make it more semantically obvious why we have to wait before invoking methods.
 *
 * https://github.com/sveltejs/svelte/issues/8954
 * https://github.com/sveltejs/svelte/pull/8457
 */
export function tick() {
    return Promise.resolve();
}

function createDispatchFilterWithParser(
    subject: Subject<ExternalAppAction>,
    parser: (value: unknown) => FilterValue | null,
    sender = '',
) {
    return (item: unknown, rawValue: unknown) => {
        const value = parser(rawValue);

        if (!isString(item)) {
            logger.error(`'filterItem' must be a string`);
            return null;
        }
        if (!FilterValue.codec.is(value)) {
            logger.error(`Failed to parse filter value:`, rawValue);
            return null;
        }

        const action: ExternalAppAction = {
            ...AppAction.filters.CHANGE_FILTER_VALUE({ item, value }),
            sender,
        };

        subject.next(action);
    };
}

function isPortletCE(p: unknown): p is PortletCE {
    return (
        p instanceof HTMLElement &&
        (p.tagName.toLowerCase() === 'roi-portlet' || p.tagName.toLowerCase() === 'portlet-ce')
    );
}
